import { sha256Hex } from "../integration/local-ifc";
import { canonicalJson, canonicalJsonBytes } from "./d2-property-proposal-candidate";
import type { AnonymousFixture } from "./types";

export const D2_LOCAL_ENRICHMENT_PROPOSAL_VERSION = "workbench-local-enrichment-proposal/1" as const;
export const D2_LOCAL_ENRICHMENT_TARGET_CAP = 1_024 as const;

export type D2LocalPropertyOperationKind = "property-add" | "property-replace" | "property-clear" | "property-remove";
export type D2LocalPropertyState =
  | { readonly state: "missing" }
  | { readonly state: "present"; readonly value: unknown };

export interface D2LocalPropertyOperationInput {
  readonly kind: D2LocalPropertyOperationKind;
  readonly elementId: string;
  readonly property: string;
  readonly before: D2LocalPropertyState;
  readonly value?: string | number | boolean;
}

export type D2LocalEnrichmentRefusalReason =
  | "duplicate-target"
  | "missing-element"
  | "read-only-element"
  | "geometry-property"
  | "before-mismatch"
  | "mixed-type"
  | "invalid-operation"
  | "already-satisfied"
  | "population-refused";

export interface D2LocalEnrichmentTargetOutcome {
  readonly elementId: string;
  readonly property: string;
  readonly kind: D2LocalPropertyOperationKind;
  readonly status: "proposed" | "refused";
  readonly refusalReason?: D2LocalEnrichmentRefusalReason;
}

export interface D2LocalEnrichmentOperation {
  readonly opId: string;
  readonly order: number;
  readonly kind: D2LocalPropertyOperationKind;
  readonly elementId: string;
  readonly property: string;
  readonly before: D2LocalPropertyState;
  readonly after:
    | { readonly state: "present"; readonly value: string | number | boolean }
    | { readonly state: "cleared" }
    | { readonly state: "removed" };
  readonly writerAdmission: "supported-current-provider" | "unsupported-current-provider";
}

export interface D2LocalEnrichmentProposal {
  readonly contractVersion: typeof D2_LOCAL_ENRICHMENT_PROPOSAL_VERSION;
  readonly status: "local-empty" | "local-proposal" | "local-refusal";
  readonly authority: "none";
  readonly source: {
    readonly datasetId: string;
    readonly byteLength: number;
    readonly sha256: string;
    readonly bytesPreserved: true;
  };
  readonly selectedPopulation: {
    readonly selected: number;
    readonly proposed: number;
    readonly refused: number;
    readonly exactClosure: true;
    readonly checkedCap: typeof D2_LOCAL_ENRICHMENT_TARGET_CAP;
    readonly checkedCapIsProductionLimit: false;
    readonly silentPartialApply: false;
  };
  readonly writerAdmission: {
    readonly supported: number;
    readonly unsupported: number;
    readonly exactClosure: true;
    readonly unsupportedOperationsWereApplied: false;
  };
  readonly targetOutcomes: readonly D2LocalEnrichmentTargetOutcome[];
  readonly operations: readonly D2LocalEnrichmentOperation[];
  readonly validation: { readonly state: "partial"; readonly authoritativeVerdict: false };
  readonly preservationAssertions: {
    readonly sourceBytesUnchanged: true;
    readonly fixtureUnchanged: true;
    readonly unknownRecordsRetained: true;
  };
}

export interface D2LocalEnrichmentAuthoringInput {
  readonly fixture: AnonymousFixture;
  readonly source: { readonly byteLength: number; readonly sha256: string };
  readonly targets: readonly D2LocalPropertyOperationInput[];
}

const geometryProperties = new Set(["height", "width", "length", "area", "volume", "geometry", "thickness"]);
const SOURCE_DIGEST = /^[0-9a-f]{64}$/;

function stableTargets(targets: readonly D2LocalPropertyOperationInput[]): readonly D2LocalPropertyOperationInput[] {
  return [...targets].sort((left, right) =>
    left.elementId.localeCompare(right.elementId) ||
    left.property.localeCompare(right.property) ||
    left.kind.localeCompare(right.kind)
  );
}

function duplicateIndexes(targets: readonly D2LocalPropertyOperationInput[]): ReadonlySet<number> {
  const firstIndexByTarget = new Map<string, number>();
  const duplicates = new Set<number>();
  targets.forEach((target, index) => {
    const key = `${target.elementId}\u0000${target.property.trim()}`;
    const firstIndex = firstIndexByTarget.get(key);
    if (firstIndex === undefined) firstIndexByTarget.set(key, index);
    else {
      duplicates.add(firstIndex);
      duplicates.add(index);
    }
  });
  return duplicates;
}

function propertyState(properties: Readonly<Record<string, unknown>>, property: string): D2LocalPropertyState {
  return Object.prototype.hasOwnProperty.call(properties, property)
    ? { state: "present", value: structuredClone(properties[property]) }
    : { state: "missing" };
}

function equalState(left: D2LocalPropertyState, right: D2LocalPropertyState): boolean {
  return canonicalJson(left) === canonicalJson(right);
}

function primitive(value: unknown): value is string | number | boolean {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function validateTarget(
  fixture: AnonymousFixture,
  input: D2LocalPropertyOperationInput
): D2LocalEnrichmentRefusalReason | null {
  const property = input.property.trim();
  if (input.elementId === "" || property === "") return "invalid-operation";
  const element = fixture.elements.find((candidate) => candidate.id === input.elementId);
  if (element === undefined) return "missing-element";
  if (!element.editable || element.unsupportedReason !== undefined) return "read-only-element";
  if (geometryProperties.has(property.toLowerCase())) return "geometry-property";
  const actual = propertyState(element.properties, property);
  if (!equalState(input.before, actual)) return "before-mismatch";

  if (input.kind === "property-add") {
    if (actual.state !== "missing" || !primitive(input.value)) return "invalid-operation";
    return null;
  }
  if (input.kind === "property-replace") {
    if (actual.state !== "present" || !primitive(actual.value) || !primitive(input.value)) return "invalid-operation";
    if (typeof actual.value !== typeof input.value) return "mixed-type";
    if (Object.is(actual.value, input.value)) return "already-satisfied";
    return null;
  }
  if (input.kind === "property-clear" || input.kind === "property-remove") {
    if (actual.state !== "present" || input.value !== undefined) return "invalid-operation";
    return null;
  }
  return "invalid-operation";
}

function targetOutcome(
  input: D2LocalPropertyOperationInput,
  status: "proposed" | "refused",
  refusalReason?: D2LocalEnrichmentRefusalReason
): D2LocalEnrichmentTargetOutcome {
  return {
    elementId: input.elementId,
    property: input.property.trim(),
    kind: input.kind,
    status,
    ...(refusalReason === undefined ? {} : { refusalReason })
  };
}

function afterState(input: D2LocalPropertyOperationInput): D2LocalEnrichmentOperation["after"] {
  if (input.kind === "property-add" || input.kind === "property-replace") {
    return { state: "present", value: input.value! };
  }
  return input.kind === "property-clear" ? { state: "cleared" } : { state: "removed" };
}

export function assertD2LocalEnrichmentTargetCount(count: number): void {
  if (!Number.isSafeInteger(count) || count < 0) {
    throw new Error("Local enrichment target count must be a non-negative safe integer.");
  }
  if (count > D2_LOCAL_ENRICHMENT_TARGET_CAP) {
    throw new Error(`Local enrichment proposal exceeds the checked cap of ${D2_LOCAL_ENRICHMENT_TARGET_CAP} targets.`);
  }
}

export async function authorD2LocalEnrichmentProposal(
  input: D2LocalEnrichmentAuthoringInput
): Promise<D2LocalEnrichmentProposal> {
  assertD2LocalEnrichmentTargetCount(input.targets.length);
  if (!Number.isSafeInteger(input.source.byteLength) || input.source.byteLength < 0 || !SOURCE_DIGEST.test(input.source.sha256)) {
    throw new Error("Local enrichment source receipt is invalid.");
  }
  const targets = stableTargets(input.targets);
  const duplicates = duplicateIndexes(targets);
  const reasons = new Map<number, D2LocalEnrichmentRefusalReason>();
  targets.forEach((target, index) => {
    const reason = duplicates.has(index) ? "duplicate-target" : validateTarget(input.fixture, target);
    if (reason !== null) reasons.set(index, reason);
  });
  const refused = reasons.size > 0;
  const empty = targets.length === 0;
  const operations = refused ? [] : await Promise.all(targets.map(async (target, order): Promise<D2LocalEnrichmentOperation> => {
    const after = afterState(target);
    return {
      opId: `op-sha256-${await sha256Hex(canonicalJsonBytes({
        sourceSha256: input.source.sha256,
        datasetId: input.fixture.id,
        elementId: target.elementId,
        property: target.property.trim(),
        kind: target.kind,
        before: target.before,
        after
      }))}`,
      order,
      kind: target.kind,
      elementId: target.elementId,
      property: target.property.trim(),
      before: structuredClone(target.before),
      after: structuredClone(after),
      writerAdmission: target.kind === "property-replace"
        ? "supported-current-provider"
        : "unsupported-current-provider"
    };
  }));
  const supported = operations.filter((operation) => operation.writerAdmission === "supported-current-provider").length;
  const targetOutcomes = targets.map((target, index) => {
    if (!refused) return targetOutcome(target, "proposed");
    return targetOutcome(target, "refused", reasons.get(index) ?? "population-refused");
  });

  return {
    contractVersion: D2_LOCAL_ENRICHMENT_PROPOSAL_VERSION,
    status: empty ? "local-empty" : refused ? "local-refusal" : "local-proposal",
    authority: "none",
    source: {
      datasetId: input.fixture.id,
      byteLength: input.source.byteLength,
      sha256: input.source.sha256,
      bytesPreserved: true
    },
    selectedPopulation: {
      selected: targets.length,
      proposed: refused ? 0 : targets.length,
      refused: refused ? targets.length : 0,
      exactClosure: true,
      checkedCap: D2_LOCAL_ENRICHMENT_TARGET_CAP,
      checkedCapIsProductionLimit: false,
      silentPartialApply: false
    },
    writerAdmission: {
      supported,
      unsupported: operations.length - supported,
      exactClosure: true,
      unsupportedOperationsWereApplied: false
    },
    targetOutcomes,
    operations,
    validation: { state: "partial", authoritativeVerdict: false },
    preservationAssertions: {
      sourceBytesUnchanged: true,
      fixtureUnchanged: true,
      unknownRecordsRetained: true
    }
  };
}
